import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, NETWORK } from "../data/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-black/60 backdrop-blur-2xl backdrop-saturate-150 border-b border-white/10"
            : "bg-transparent"
        }`}
      >
        <nav className="max-w-[1600px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <img
              src={NETWORK.logo}
              alt="EKG Network"
              className="w-11 h-11 rounded-full object-cover ring-1 ring-secondary/50 group-hover:ring-secondary transition-all duration-300"
            />
            <span className="hidden sm:block leading-none">
              <span className="block font-display uppercase text-sm tracking-wide text-foreground">
                Encounter Kingdom
              </span>
              <span className="block overline text-secondary text-[0.6rem]">Glory Network</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={`overline text-[0.7rem] transition-colors duration-300 hover:text-secondary ${
                  pathname === n.to ? "text-secondary" : "text-muted-foreground"
                }`}
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/summit"
              data-testid="nav-register"
              className="overline text-[0.7rem] bg-primary text-primary-foreground px-5 py-3 hover:bg-primary/85 transition-colors duration-300"
            >
              Register
            </Link>
          </div>

          <button
            data-testid="nav-menu-toggle"
            className="lg:hidden text-foreground"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-background lg:hidden pt-24 px-8"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-2">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.06 }}
                >
                  <Link
                    to={n.to}
                    className="font-display uppercase text-4xl text-foreground py-3 block border-b border-border hover:text-primary transition-colors"
                  >
                    {n.label}
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
